/**
 * FamilyTreeView — branch-separated family tree.
 *
 * Geometry, top to bottom:
 *
 *   [focal's ancestors]               [partner's ancestors]
 *   (real people only, branches stop  (same on the right)
 *    when a parent is unknown)
 *                      [focal] — [partner]
 *                              |
 *            [descendants tree, recursive: children,
 *             grandchildren, great-grandchildren, …]
 *
 * Both ancestor subtrees are independent d3.tree layouts so that Hans's
 * father-line cannot visually cross Marijke's mother-line. Descendants are
 * a third d3.tree rooted at the focal and rendered downwards — every real
 * descendant is drawn, not just the first generation, so a user's POV (a
 * great-great-great-grandchild) is on the same canvas as an ancestor up top
 * instead of hidden behind a dozen clicks.
 *
 * Placeholders ("Unknown ancestor") live in the Pedigree view. Family Tree
 * only shows real persons so the two views don't look identical when
 * ancestry is sparse.
 */
import { useEffect, useRef, useCallback, useMemo, useState } from 'react'
import * as d3 from 'd3'
import { useTreeStore } from '../../stores/tree.js'
import { extractYear } from '../../utils/date.js'
import { mediaUrl, API_BASE_URL } from '../../services/api.js'
import './TreeView.css'

const NODE_W       = 170
const NODE_H       = 64
const H_GAP        = 20
const V_GAP        = 80
const SIDE_GAP     = 80    // horizontal gap between the two ancestor pedigrees
const CHILD_V_GAP  = 60    // gap below the couple before children row
const MAX_ANC_DEPTH = 25   // hard cap when building — actual visible depth is user-controlled
const MAX_DESC_DEPTH = 25  // hard cap — actual visible depth is user-controlled

const FOCUSED_ANC = 3      // default ancestor depth in Focused mode
const FOCUSED_DESC = 2     // default descendant depth in Focused mode

export default function FamilyTreeView({ persons, relationships, onPersonClick, onAddRelated }) {
  const svgRef = useRef(null)
  const treeStateRef = useRef(null)
  const focalPersonId = useTreeStore(s => s.focalPersonId)
  const setFocalPerson = useTreeStore(s => s.setFocalPerson)

  const onPersonClickRef = useRef(onPersonClick)
  const setFocalPersonRef = useRef(setFocalPerson)
  const onAddRelatedRef = useRef(onAddRelated)
  useEffect(() => { onPersonClickRef.current = onPersonClick }, [onPersonClick])
  useEffect(() => { setFocalPersonRef.current = setFocalPerson }, [setFocalPerson])
  useEffect(() => { onAddRelatedRef.current = onAddRelated }, [onAddRelated])

  // Pre-compute, per person, how many parents are recorded (cap the +parent
  // affordance at 2) and whether they have a partner. Children are uncapped
  // — most people have multiple, so the +child button always shows.
  const rels = useMemo(() => {
    const parentCount = new Map(), hasPartner = new Set()
    for (const r of (relationships || [])) {
      if (r.type === 'parent_child') {
        parentCount.set(r.person_b_id, (parentCount.get(r.person_b_id) || 0) + 1)
      } else if (r.type === 'partner') {
        hasPartner.add(r.person_a_id)
        hasPartner.add(r.person_b_id)
      }
    }
    return { parentCount, hasPartner }
  }, [relationships])
  const relsRef = useRef(rels)
  useEffect(() => { relsRef.current = rels }, [rels])

  // View controls — Focused (default, readable window) vs All (full tree).
  // In Focused mode the slider scrolls through time by widening/narrowing
  // the visible ancestor window.
  const [mode, setMode] = useState('focused')
  const [ancDepth, setAncDepth] = useState(FOCUSED_ANC)

  // Search — first against this tree's persons (client-side), then fall back
  // to the global public search so users can discover people outside their
  // tree. Keep results minimal; this isn't a full directory browser.
  const [searchQuery, setSearchQuery] = useState('')
  const [globalResults, setGlobalResults] = useState([])
  const [searchOpen, setSearchOpen] = useState(false)
  const q = searchQuery.trim().toLowerCase()
  const localMatches = useMemo(() => {
    if (q.length < 2) return []
    return persons.filter(p => {
      const hay = [p.first_name, p.preferred_name, p.last_name, p.birth_place]
        .filter(Boolean).join(' ').toLowerCase()
      return hay.includes(q)
    }).slice(0, 8)
  }, [persons, q])
  useEffect(() => {
    if (q.length < 2) { setGlobalResults([]); return }
    // Only bother hitting global search if local is thin.
    if (localMatches.length >= 5) { setGlobalResults([]); return }
    const ctrl = new AbortController()
    const localIds = new Set(persons.map(p => p.id))
    fetch(`${API_BASE_URL}/public/search?q=${encodeURIComponent(q)}`, { signal: ctrl.signal })
      .then(r => r.ok ? r.json() : { results: [] })
      .then(data => {
        // Drop results already present in this tree and living persons.
        const r = (data.results || []).filter(x => !x.living && !localIds.has(x.id)).slice(0, 5)
        setGlobalResults(r)
      })
      .catch(() => {})
    return () => ctrl.abort()
  }, [q, localMatches.length, persons])

  // Resolve focal + primary partner + descendants tree from the data.
  const model = useMemo(
    () => buildModel(focalPersonId, persons, relationships),
    [focalPersonId, persons, relationships]
  )

  const visibleAnc  = mode === 'focused' ? ancDepth : MAX_ANC_DEPTH
  const visibleDesc = mode === 'focused' ? FOCUSED_DESC : MAX_DESC_DEPTH

  const render = useCallback(() => {
    if (!svgRef.current) return
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const W = svgRef.current.clientWidth || 900
    const H = svgRef.current.clientHeight || 600

    if (!model || !model.focal) {
      svg.append('text')
        .attr('x', W / 2).attr('y', H / 2)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9ca3af').attr('font-size', '14px')
        .text(persons.length === 0
          ? 'No persons yet. Add your first ancestor.'
          : 'Pick a person to anchor the family tree.')
      return
    }

    const g = svg.append('g')

    // Timeline overlay — drawn above the zoom group, fixed to screen coords.
    // Updated on every zoom/pan tick so the year ticks stay next to the
    // generations they describe.
    const timelineG = svg.append('g').attr('class', 'timeline-overlay')
    const TL_W = 68  // timeline strip width

    const zoom = d3.zoom()
      .scaleExtent([0.15, 2.5])
      .on('zoom', (e) => {
        g.attr('transform', e.transform)
        drawTimeline(e.transform)
      })
    svg.call(zoom)

    // ── Lay out focal's ancestors (left) and partner's ancestors (right) ─────
    // depthOrParents / depthOrChildren prune so deeper nodes aren't traversed.
    const ancAccess = d => (d.__depth ?? 0) >= visibleAnc ? [] : (d.parents || [])
    const descAccess = d => (d.__depth ?? 0) >= visibleDesc ? [] : (d.children || [])
    tagDepth(model.leftTree,  d => d.parents, 0)
    if (model.rightTree) tagDepth(model.rightTree, d => d.parents, 0)
    if (model.descendantsTree) tagDepth(model.descendantsTree, d => d.children, 0)

    const leftHierarchy = d3.hierarchy(model.leftTree, ancAccess)
    const rightHierarchy = model.rightTree
      ? d3.hierarchy(model.rightTree, ancAccess)
      : null

    const ancLayout = d3.tree()
      .nodeSize([NODE_W + H_GAP, NODE_H + V_GAP])
      .separation((a, b) => a.parent === b.parent ? 1 : 1.15)

    ancLayout(leftHierarchy)
    if (rightHierarchy) ancLayout(rightHierarchy)

    // Flip Y so the root (focal) sits at y=0 and ancestors are above (negative y).
    const flipUp = (h) => { h.each(d => { d.y = -d.y }) }
    flipUp(leftHierarchy)
    if (rightHierarchy) flipUp(rightHierarchy)

    // Normalise X: each side's root sits at its own anchor x.
    const shiftToRoot = (h, anchorX) => {
      const rootX = h.descendants()[0].x
      const dx = anchorX - rootX
      h.each(d => { d.x += dx })
    }

    // Couple positioning rule: men on the left, women on the right.
    // We only flip from the focal-on-left default when the focal is
    // female AND the partner is male — every other combination keeps
    // the historical ordering (focal-on-left), which already satisfies
    // the rule for male focals and is the safe fallback for same-sex
    // or unknown-gender couples.
    const halfCoupleWidth = (NODE_W + SIDE_GAP) / 2
    const flipCouple =
      model.partner &&
      model.focal?.gender === 'female' &&
      model.partner?.gender === 'male'
    let focalAnchorX   = model.partner ? (flipCouple ?  halfCoupleWidth : -halfCoupleWidth) : 0
    let partnerAnchorX =                  flipCouple ? -halfCoupleWidth :  halfCoupleWidth
    shiftToRoot(leftHierarchy, focalAnchorX)
    if (rightHierarchy) shiftToRoot(rightHierarchy, partnerAnchorX)

    // Push the two ancestor trees apart if wide branches would otherwise
    // overlap in the middle. d3.tree lays each side independently around
    // its root, so a large paternal grandparent cluster on the left can
    // happily sit at x > 0 — right where the partner's ancestors live.
    // Detect that and shift both sides outward so there's always SIDE_GAP
    // of breathing room between their closest cards.
    if (rightHierarchy) {
      const leftMaxX  = d3.max(leftHierarchy.descendants(),  d => d.x) + NODE_W / 2
      const rightMinX = d3.min(rightHierarchy.descendants(), d => d.x) - NODE_W / 2
      const overlap = leftMaxX - rightMinX + SIDE_GAP  // positive → they cross
      if (overlap > 0) {
        const push = overlap / 2
        leftHierarchy.each(d => { d.x -= push })
        rightHierarchy.each(d => { d.x += push })
        focalAnchorX   -= push
        partnerAnchorX += push
      }
    }

    // ── Lay out descendants (downward from focal) ───────────────────────────
    const descHierarchy = model.descendantsTree
      ? d3.hierarchy(model.descendantsTree, descAccess)
      : null

    const descLayout = d3.tree()
      .nodeSize([NODE_W + H_GAP, NODE_H + V_GAP])
      .separation((a, b) => a.parent === b.parent ? 1 : 1.2)

    if (descHierarchy && descHierarchy.descendants().length > 1) {
      descLayout(descHierarchy)
      // Root (focal) already lives at y=0 from ancLayout; shift descendants down.
      // We also offset so descendants hang off the couple midpoint, not the
      // focal alone, so grandchildren sit centred under the couple.
      const rootX = descHierarchy.x
      const coupleMidX = model.partner
        ? (focalAnchorX + partnerAnchorX) / 2
        : focalAnchorX
      const dx = coupleMidX - rootX
      // Shift y so the focal (root) is at y=0 + CHILD_V_GAP hop down to first level
      const rootY = descHierarchy.y
      descHierarchy.each(d => {
        d.x += dx
        d.y = d.y - rootY  // root → 0
      })
      // Now descendants go downward (positive y). Push them below the couple
      // by CHILD_V_GAP so there's breathing room.
      descHierarchy.each(d => {
        if (d.depth > 0) d.y += CHILD_V_GAP
      })
    }

    // ── Draw ancestor edges ─────────────────────────────────────────────────
    const edgeLayer = g.append('g').attr('class', 'edges')
    const link = d3.linkVertical()
      .source(d => ({ x: d.source.x, y: d.source.y + NODE_H / 2 }))
      .target(d => ({ x: d.target.x, y: d.target.y - NODE_H / 2 }))
      .x(d => d.x).y(d => d.y)

    function drawLinks(h, opts = {}) {
      const { skipRoot = false } = opts
      const data = skipRoot
        ? h.links().filter(l => l.source !== h)  // hide focal-to-parents curves; bracket replaces them
        : h.links()
      edgeLayer.selectAll(null)
        .data(data)
        .enter().append('path')
        .attr('d', link)
        .attr('fill', 'none')
        .attr('stroke', '#cfc7b6')
        .attr('stroke-width', 1.2)
        .attr('opacity', 0.85)
    }
    // When focal has siblings rendered on the focal row, the parents-to-focal
    // connection is replaced with a bracket-style joinery (drawn below) so
    // siblings share one clean horizontal bar instead of three crisscrossing
    // curves converging on a single point.
    const willDrawFocalBracket = (model.siblings || []).length > 0 && (leftHierarchy.children?.length || 0) > 0
    drawLinks(leftHierarchy, { skipRoot: willDrawFocalBracket })
    if (rightHierarchy) drawLinks(rightHierarchy)

    // ── Descendant edges (elbow style, parent-below-to-child) ───────────────
    if (descHierarchy && descHierarchy.descendants().length > 1) {
      for (const ln of descHierarchy.links()) {
        const from = { x: ln.source.x, y: ln.source.y + NODE_H / 2 }
        const to   = { x: ln.target.x, y: ln.target.y - NODE_H / 2 }
        const midY = to.y - Math.min(24, (to.y - from.y) / 2)
        edgeLayer.append('path')
          .attr('d', `M${from.x},${from.y} L${from.x},${midY} L${to.x},${midY} L${to.x},${to.y}`)
          .attr('fill', 'none')
          .attr('stroke', '#9ca3af').attr('stroke-width', 1.2).attr('opacity', 0.75)
      }
    }

    // ── Partner line between the two roots ──────────────────────────────────
    const focalNode = leftHierarchy.descendants().find(d => d.data.person?.id === model.focal.id)
    const partnerNode = rightHierarchy?.descendants().find(d => d.data.person?.id === model.partner?.id)
    if (focalNode && partnerNode) {
      // Draw between the two cards' inner edges regardless of which
      // person ended up on the visual left after the gender-based flip.
      const leftN  = focalNode.x <= partnerNode.x ? focalNode  : partnerNode
      const rightN = focalNode.x <= partnerNode.x ? partnerNode : focalNode
      edgeLayer.append('line')
        .attr('x1', leftN.x  + NODE_W / 2).attr('y1', leftN.y)
        .attr('x2', rightN.x - NODE_W / 2).attr('y2', rightN.y)
        .attr('stroke', '#c8a56e').attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '4,3').attr('opacity', 0.65)
    }

    // ── "You are here" divider above the focal row ─────────────────────────
    // Subtle horizontal rule separating ancestors (above) from focal + their
    // descendants (below). Sits halfway between the focal and the parent row.
    const allNodes = [
      ...leftHierarchy.descendants(),
      ...(rightHierarchy ? rightHierarchy.descendants() : []),
      ...(descHierarchy ? descHierarchy.descendants() : []),
    ]
    if (allNodes.length) {
      const minX = d3.min(allNodes, d => d.x) - NODE_W
      const maxX = d3.max(allNodes, d => d.x) + NODE_W
      const dividerY = -(NODE_H / 2 + V_GAP / 2)
      edgeLayer.append('line')
        .attr('x1', minX).attr('y1', dividerY)
        .attr('x2', maxX).attr('y2', dividerY)
        .attr('stroke', '#c8a56e').attr('stroke-width', 0.8)
        .attr('stroke-dasharray', '4,5').attr('opacity', 0.35)
        .lower()
      edgeLayer.append('text')
        .attr('x', minX + 8).attr('y', dividerY - 6)
        .attr('font-family', 'Inter, system-ui, sans-serif')
        .attr('font-size', '9px').attr('font-weight', '500')
        .attr('fill', '#9ca3af').attr('letter-spacing', '0.08em')
        .text('ANCESTORS').lower()
      edgeLayer.append('text')
        .attr('x', minX + 8).attr('y', dividerY + 14)
        .attr('font-family', 'Inter, system-ui, sans-serif')
        .attr('font-size', '9px').attr('font-weight', '500')
        .attr('fill', '#9ca3af').attr('letter-spacing', '0.08em')
        .text('YOUR LINE').lower()
    }

    // ── Build SVG defs: photo patterns + focal glow ────────────────────────
    const defs = svg.append('defs')
    const filter = defs.append('filter').attr('id', 'ft-focal-glow')
      .attr('x', '-30%').attr('y', '-30%').attr('width', '160%').attr('height', '160%')
    filter.append('feGaussianBlur').attr('stdDeviation', '3').attr('result', 'b')
    const m = filter.append('feMerge')
    m.append('feMergeNode').attr('in', 'b')
    m.append('feMergeNode').attr('in', 'SourceGraphic')

    for (const p of persons) {
      if (!p.photo_thumb_key) continue
      const patId = `ft-photo-${p.id.replace(/-/g, '')}`
      defs.append('pattern')
        .attr('id', patId)
        .attr('patternUnits', 'objectBoundingBox')
        .attr('width', 1).attr('height', 1)
        .append('image')
        .attr('href', mediaUrl(p.photo_thumb_key))
        .attr('width', 36).attr('height', 36)
        .attr('preserveAspectRatio', 'xMidYMid slice')
    }

    // ── Draw nodes ───────────────────────────────────────────────────────────
    const nodeLayer = g.append('g').attr('class', 'nodes')
    const drawnPersonIds = new Set()

    function drawPedigreeNode(d) {
      if (!d.data.person) return  // defensive: no placeholder in Family Tree
      drawPerson(d.data.person, d.x, d.y)
      drawnPersonIds.add(d.data.person.id)
    }
    leftHierarchy.descendants().forEach(drawPedigreeNode)
    if (rightHierarchy) rightHierarchy.descendants().forEach(drawPedigreeNode)

    // Draw descendants — skip the root since focal is already drawn above.
    if (descHierarchy) {
      descHierarchy.descendants().forEach(d => {
        if (!d.data.person) return
        if (drawnPersonIds.has(d.data.person.id)) return
        drawPerson(d.data.person, d.x, d.y)
        drawnPersonIds.add(d.data.person.id)
      })
    }

    // ── Draw focal's siblings on the focal row ─────────────────────────────
    // Without this, focusing on a "leaf of the line" person (someone with
    // no children) makes the tree look stranded — there's no visual hint
    // that other branches of the same generation exist. Siblings render
    // as standalone cards on focal's outer side. The connection back to the
    // parents is a bracket: one drop from the parents midpoint to a
    // horizontal sibling bar, then verticals from the bar down to focal +
    // each sibling. This reads cleanly in both Focused and All modes,
    // matches the rest of the tree's line style (solid #cfc7b6), and
    // avoids the crisscrossing curves you get when every child has its
    // own independent curve back to one shared point.
    const siblings = (model.siblings || []).filter(s => !drawnPersonIds.has(s.id))
    if (willDrawFocalBracket || siblings.length === 0) {
      const parentNodes = leftHierarchy.children || []
      if (parentNodes.length > 0) {
        const focalIsOnRight = !!model.partner && focalAnchorX > partnerAnchorX
        const siblingDir = focalIsOnRight ? +1 : -1

        // Children of these parents, in canvas order.
        const childPositions = [
          { x: focalAnchorX, y: 0 },
          ...siblings.map((_, i) => ({
            x: focalAnchorX + siblingDir * (i + 1) * (NODE_W + H_GAP),
            y: 0,
          })),
        ]

        const parentsMidX = d3.mean(parentNodes, d => d.x)
        const parentsBottomY = d3.mean(parentNodes, d => d.y) + NODE_H / 2
        const childrenTopY = -NODE_H / 2
        const barY = (parentsBottomY + childrenTopY) / 2

        if (siblings.length > 0) {
          // Bracket style — only when there's more than the focal under this
          // parent pair. Solo focal keeps the existing per-parent curves
          // (already drawn by drawLinks above) for a tighter "Y" look.
          const xs = [parentsMidX, ...childPositions.map(c => c.x)]
          const minBarX = Math.min(...xs)
          const maxBarX = Math.max(...xs)

          edgeLayer.append('line')
            .attr('x1', minBarX).attr('y1', barY)
            .attr('x2', maxBarX).attr('y2', barY)
            .attr('stroke', '#cfc7b6').attr('stroke-width', 1.2).attr('opacity', 0.85)

          edgeLayer.append('line')
            .attr('x1', parentsMidX).attr('y1', parentsBottomY)
            .attr('x2', parentsMidX).attr('y2', barY)
            .attr('stroke', '#cfc7b6').attr('stroke-width', 1.2).attr('opacity', 0.85)

          for (const c of childPositions) {
            edgeLayer.append('line')
              .attr('x1', c.x).attr('y1', barY)
              .attr('x2', c.x).attr('y2', childrenTopY)
              .attr('stroke', '#cfc7b6').attr('stroke-width', 1.2).attr('opacity', 0.85)
          }
        }

        siblings.forEach((sib, i) => {
          const pos = childPositions[i + 1]
          drawPerson(sib, pos.x, pos.y)
          drawnPersonIds.add(sib.id)
        })
      }
    }

    function drawPerson(person, cx, cy) {
      const isFocal   = person.id === model.focal?.id
      const isPartner = person.id === model.partner?.id
      const isFamily  = isFocal || isPartner
      const stroke = isFocal ? '#c8a56e' : (isPartner ? '#c8a56e' : '#e5e0d5')
      const strokeW = isFocal ? 2.2 : (isPartner ? 1.7 : 1)

      const node = nodeLayer.append('g')
        .attr('transform', `translate(${cx - NODE_W / 2},${cy - NODE_H / 2})`)
        .attr('cursor', 'pointer')
        .on('click', () => {
          setFocalPersonRef.current(person.id)
          onPersonClickRef.current(person.id)
        })
        .on('mouseenter', () => {
          addBtnsGroup.style('opacity', 1).style('pointer-events', 'auto')
        })
        .on('mouseleave', () => {
          addBtnsGroup.style('opacity', 0).style('pointer-events', 'none')
        })

      // Add-related '+' buttons layer — ghost buttons on top/right/bottom
      // edges for missing parent/partner/child slots. Appended AFTER the
      // main card so they sit above it for hit-testing.
      const r = relsRef.current || { parentCount: new Map(), hasPartner: new Set() }
      const canAddParent  = (r.parentCount.get(person.id) || 0) < 2
      const canAddPartner = true  // remarriages/second partners are always possible
      const canAddChild   = true  // people can have any number of children

      const addBtnsGroup = node.append('g')
        .attr('class', 'ft-add-btns')
        .style('opacity', 0)
        .style('pointer-events', 'none')
        .style('transition', 'opacity 150ms')

      function drawAddBtn(x, y, relation, label) {
        const g = addBtnsGroup.append('g')
          .attr('transform', `translate(${x},${y})`)
          .attr('cursor', 'pointer')
          .on('click', (ev) => {
            ev.stopPropagation()
            if (onAddRelatedRef.current) onAddRelatedRef.current(person.id, relation)
          })
        // Outer halo — barely there at rest, lifts to a soft glow on hover.
        const halo = g.append('circle')
          .attr('r', 13)
          .attr('fill', '#fffdf8')
          .attr('stroke', '#c8a56e')
          .attr('stroke-width', 1)
          .attr('opacity', 0.9)
          .style('filter', 'drop-shadow(0 1px 2px rgba(0,0,0,0.06))')
          .style('transition', 'all 160ms')
        const plus = g.append('text')
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('font-size', '15px')
          .attr('font-weight', '300')
          .attr('fill', '#3b6b5a')
          .style('pointer-events', 'none')
          .text('+')
        g.on('mouseenter', function () {
          halo.attr('fill', '#3b6b5a').attr('stroke', '#3b6b5a').attr('r', 14)
          plus.attr('fill', '#fff').attr('font-weight', '500')
        }).on('mouseleave', function () {
          halo.attr('fill', '#fffdf8').attr('stroke', '#c8a56e').attr('r', 13)
          plus.attr('fill', '#3b6b5a').attr('font-weight', '300')
        })
        g.append('title').text(label)
      }

      if (canAddParent)  drawAddBtn(NODE_W / 2, -3,          'parent',  'Add parent')
      if (canAddChild)   drawAddBtn(NODE_W / 2, NODE_H + 3,  'child',   'Add child')
      if (canAddPartner) drawAddBtn(NODE_W + 3, NODE_H / 2,  'partner', 'Add partner')

      node.append('rect')
        .attr('width', NODE_W).attr('height', NODE_H)
        .attr('rx', 6)
        .attr('fill', isFocal ? '#fffdf8' : '#ffffff')
        .attr('stroke', stroke).attr('stroke-width', strokeW)
        .attr('filter', isFocal ? 'url(#ft-focal-glow)' : null)

      const accent = person.gender === 'male' ? '#3b6b5a'
        : person.gender === 'female' ? '#c8a56e'
        : '#9ca3af'
      node.append('rect')
        .attr('width', 3).attr('height', NODE_H)
        .attr('rx', 3).attr('fill', accent)

      const hasPhoto = !!person.photo_thumb_key
      const maxChars = hasPhoto ? 13 : 19

      const given = person.preferred_name || (person.first_name ? person.first_name.split(' ')[0] : '') || 'Unknown'
      node.append('text')
        .attr('x', 14).attr('y', 21)
        .attr('font-size', '13px').attr('font-weight', '600')
        .attr('fill', '#1a1a1a')
        .text(truncate(given, maxChars))

      if (person.last_name) {
        node.append('text')
          .attr('x', 14).attr('y', 36)
          .attr('font-size', '11.5px').attr('font-weight', '500')
          .attr('fill', '#374151')
          .text(truncate(person.last_name, maxChars + 2))
      }

      const by = extractYear(person.birth_date)
      const dy = extractYear(person.death_date)
      const yr = by ? (dy ? `${by} – ${dy}` : `b. ${by}`) : ''
      if (yr) {
        node.append('text')
          .attr('x', 14).attr('y', 52)
          .attr('font-size', '10px').attr('fill', '#6b7280')
          .text(yr)
      }

      if (hasPhoto) {
        const patId = `ft-photo-${person.id.replace(/-/g, '')}`
        const photoSize = 36
        node.append('circle')
          .attr('cx', NODE_W - photoSize / 2 - 8).attr('cy', NODE_H / 2)
          .attr('r', photoSize / 2)
          .attr('fill', `url(#${patId})`)
          .attr('stroke', '#e5e0d5').attr('stroke-width', 1)
      }

      // Multi-partner chip: when the focal has >1 partner rel, surface
      // it as a small ⚭N badge in the card corner so the tree view's
      // single-line simplification doesn't hide the fact that there
      // are remarriages. Other partners live on the profile page.
      if (isFocal && model.partnerCount > 1) {
        const chipX = NODE_W - 22, chipY = 8
        node.append('rect')
          .attr('x', chipX).attr('y', chipY)
          .attr('width', 18).attr('height', 14)
          .attr('rx', 7)
          .attr('fill', '#c8a56e')
        node.append('text')
          .attr('x', chipX + 9).attr('y', chipY + 10)
          .attr('text-anchor', 'middle')
          .attr('font-size', '9px').attr('font-weight', '700')
          .attr('fill', '#fff')
          .text(`⚭${model.partnerCount}`)
        node.append('title').text(`${model.partnerCount} partners — see profile for all`)
      }
    }

    // ── Timeline strip (year per generation, left side) ─────────────────────
    // Collects every positioned node, groups by world-y (one per generation),
    // and renders a tick + avg birth year. Redrawn on zoom/pan so years stay
    // aligned with their rows.
    const allPositioned = [
      ...leftHierarchy.descendants().map(d => ({ y: d.y, person: d.data.person })),
      ...(rightHierarchy ? rightHierarchy.descendants().map(d => ({ y: d.y, person: d.data.person })) : []),
      ...(descHierarchy ? descHierarchy.descendants().map(d => ({ y: d.y, person: d.data.person })) : []),
    ].filter(n => n.person)

    // Bucket by rounded-y so neighbouring rows don't double up.
    const yBuckets = new Map()
    for (const n of allPositioned) {
      const key = Math.round(n.y / (NODE_H + V_GAP))
      if (!yBuckets.has(key)) yBuckets.set(key, { y: n.y, years: [] })
      const year = Number(extractYear(n.person.birth_date))
      if (year) yBuckets.get(key).years.push(year)
    }
    const genRows = [...yBuckets.values()]
      .map(b => ({ y: b.y, avgYear: b.years.length ? Math.round(b.years.reduce((a, x) => a + x, 0) / b.years.length) : null }))
      .sort((a, b) => a.y - b.y)

    function drawTimeline(transform) {
      timelineG.selectAll('*').remove()
      if (!genRows.length) return

      // Background strip
      timelineG.append('rect')
        .attr('x', 0).attr('y', 0).attr('width', TL_W).attr('height', H)
        .attr('fill', 'var(--warm-white, #fdfaf4)').attr('opacity', 0.94)
      // Spine
      timelineG.append('line')
        .attr('x1', TL_W - 1).attr('y1', 0).attr('x2', TL_W - 1).attr('y2', H)
        .attr('stroke', '#e5e0d5').attr('stroke-width', 1)

      const currentYear = new Date().getFullYear()
      const eraThreshold = currentYear - 80

      for (const row of genRows) {
        const screenY = row.y * transform.k + transform.y
        if (screenY < -4 || screenY > H + 4) continue
        // Tick
        timelineG.append('line')
          .attr('x1', TL_W - 14).attr('y1', screenY)
          .attr('x2', TL_W - 2).attr('y2', screenY)
          .attr('stroke', '#d4cfc6').attr('stroke-width', 1)
        if (row.avgYear) {
          const isRecent = row.avgYear >= eraThreshold
          timelineG.append('text')
            .attr('x', TL_W - 18).attr('y', screenY + 4)
            .attr('text-anchor', 'end')
            .attr('font-family', 'Inter, system-ui, sans-serif')
            .attr('font-size', '11px')
            .attr('font-weight', isRecent ? '600' : '400')
            .attr('fill', isRecent ? '#3b6b5a' : '#9ca3af')
            .text(row.avgYear)
        }
      }
    }

    // ── Initial fit-to-view ──────────────────────────────────────────────────
    const bbox = g.node().getBBox()
    const scale = Math.min(0.95, (W - 80) / bbox.width, (H - 80) / bbox.height)
    const tx = (W - bbox.width * scale) / 2 - bbox.x * scale
    const ty = (H - bbox.height * scale) / 2 - bbox.y * scale
    const initialTransform = d3.zoomIdentity.translate(tx, ty).scale(scale)
    svg.call(zoom.transform, initialTransform)
    drawTimeline(initialTransform)

    treeStateRef.current = { svg, zoom, bbox, W, H }
  }, [model, persons, focalPersonId, visibleAnc, visibleDesc])

  useEffect(() => {
    render()
    const ro = new ResizeObserver(render)
    if (svgRef.current) ro.observe(svgRef.current.parentElement)
    return () => ro.disconnect()
  }, [render])

  const handleRecenter = useCallback(() => {
    const st = treeStateRef.current
    if (!st) return
    const { svg, zoom, bbox, W, H } = st
    const scale = Math.min(0.95, (W - 80) / bbox.width, (H - 80) / bbox.height)
    const tx = (W - bbox.width * scale) / 2 - bbox.x * scale
    const ty = (H - bbox.height * scale) / 2 - bbox.y * scale
    svg.transition().duration(450)
      .call(zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale))
  }, [])

  // Slider range — max 15 generations, but don't offer more than actually exist.
  const maxAvailableDepth = useMemo(() => {
    if (!model?.leftTree) return FOCUSED_ANC
    const depthOf = (node) => {
      const kids = node.parents || []
      if (!kids.length) return 0
      return 1 + Math.max(...kids.map(depthOf))
    }
    const left = depthOf(model.leftTree)
    const right = model.rightTree ? depthOf(model.rightTree) : 0
    return Math.max(left, right, FOCUSED_ANC)
  }, [model])

  return (
    <div className="tree-view">
      <svg ref={svgRef} width="100%" height="100%" />
      {persons.length > 0 && (
        <div className="tree-view__controls">
          <div className="tree-view__search" onBlur={(e) => {
            // Close when focus leaves the whole search widget (input + dropdown)
            if (!e.currentTarget.contains(e.relatedTarget)) setSearchOpen(false)
          }}>
            <input
              type="search"
              placeholder="Search people…"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setSearchOpen(true) }}
              onFocus={() => setSearchOpen(true)}
              aria-label="Search persons in this tree or globally"
            />
            {searchOpen && q.length >= 2 && (
              // preventDefault on mousedown keeps the input focused so the
              // button's onClick runs before onBlur closes the dropdown.
              <div
                className="tree-view__search-results"
                role="listbox"
                onMouseDown={e => e.preventDefault()}
              >
                {localMatches.length === 0 && globalResults.length === 0 && (
                  <div className="tree-view__search-empty">No matches.</div>
                )}
                {localMatches.length > 0 && (
                  <div className="tree-view__search-section">
                    <div className="tree-view__search-section-label">In this tree</div>
                    {localMatches.map(p => {
                      const label = [p.preferred_name || p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unknown'
                      const year = extractYear(p.birth_date)
                      return (
                        <button
                          key={p.id}
                          type="button"
                          role="option"
                          className="tree-view__search-row"
                          onClick={() => {
                            setFocalPersonRef.current(p.id)
                            onPersonClickRef.current(p.id)
                            setSearchOpen(false)
                            setSearchQuery('')
                          }}
                        >
                          <span className="tree-view__search-name">{label}</span>
                          {year && <span className="tree-view__search-year">{year}</span>}
                        </button>
                      )
                    })}
                  </div>
                )}
                {globalResults.length > 0 && (
                  <div className="tree-view__search-section">
                    <div className="tree-view__search-section-label">Elsewhere on Genealogic</div>
                    {globalResults.map(r => {
                      const href = r.slug ? `https://genealogic.ai/person/${r.slug}` : null
                      const body = (
                        <>
                          <span className="tree-view__search-name">{r.display_name}</span>
                          {r.years && <span className="tree-view__search-year">{r.years}</span>}
                          {r.tree_count > 0 && <span className="tree-view__search-meta">in {r.tree_count} {r.tree_count === 1 ? 'tree' : 'trees'}</span>}
                        </>
                      )
                      return href ? (
                        <a key={r.id} href={href} target="_blank" rel="noreferrer" role="option" className="tree-view__search-row">{body}</a>
                      ) : (
                        <div key={r.id} role="option" className="tree-view__search-row tree-view__search-row--disabled">{body}</div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="tree-view__mode-toggle" role="tablist" aria-label="Family Tree view mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'focused'}
              className={`tree-view__mode-btn ${mode === 'focused' ? 'active' : ''}`}
              onClick={() => setMode('focused')}
              title="Show focal + nearby generations"
            >Focused</button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'all'}
              className={`tree-view__mode-btn ${mode === 'all' ? 'active' : ''}`}
              onClick={() => setMode('all')}
              title="Show every known generation"
            >All</button>
          </div>
          {/* Always visible in Focused mode: even when the reachable
              chain is shallow (often caused by a reversed or corrupt
              parent_child edge), showing "2/2 gen" is better than
              hiding the control — the user sees they've hit the data
              ceiling rather than wondering where the slider went. */}
          {mode === 'focused' && (
            <div className="tree-view__scroller" title="Scroll through time: drag to reveal older generations. Ceiling is however many connected ancestor generations exist in the data.">
              <span className="tree-view__scroller-label">Ancestors</span>
              <input
                type="range"
                min={1}
                max={Math.max(FOCUSED_ANC, Math.min(maxAvailableDepth, MAX_ANC_DEPTH))}
                value={ancDepth}
                onChange={e => setAncDepth(Number(e.target.value))}
                aria-label="Visible ancestor generations"
              />
              <span className="tree-view__scroller-value">
                {ancDepth} / {Math.max(FOCUSED_ANC, Math.min(maxAvailableDepth, MAX_ANC_DEPTH))} gen
              </span>
            </div>
          )}
          {/* When the slider max is a data-imposed cap (not the build cap),
              surface an orphan-cluster hint so users know older ancestors that
              exist in the DB but aren't linked via parent_child are findable
              in Directory → Orphan cluster. */}
          {ancDepth === maxAvailableDepth && maxAvailableDepth < MAX_ANC_DEPTH && (
            <div className="tree-view__chain-hint" title="Your tree has older ancestors in the database that aren't linked to this chain via parent_child. Find them in Directory → Not in ancestor chain.">
              chain ends — check Directory
            </div>
          )}
          <button
            type="button"
            className="tree-view__recenter"
            onClick={handleRecenter}
            title="Fit tree to view"
          >
            Fit to view
          </button>
        </div>
      )}
      {persons.length > 0 && (
        <div className="tree-view__legend">
          <span className="legend-item"><span className="legend-dot" style={{ background: '#3b6b5a' }} />Male</span>
          <span className="legend-item"><span className="legend-dot" style={{ background: '#c8a56e' }} />Female</span>
          <span className="legend-item"><span className="legend-dot" style={{ background: '#9ca3af' }} />Unknown</span>
          <span className="legend-sep" />
          <span className="legend-item legend-dashed">- - -&nbsp; Partner</span>
        </div>
      )}
    </div>
  )
}


// ─── Model builder ─────────────────────────────────────────────────────────

function buildModel(focalId, persons, relationships) {
  const byId = new Map(persons.map(p => [p.id, p]))

  // Pick the focal. Fall back to the most recently-born person if none set.
  let focal = focalId ? byId.get(focalId) : null
  if (!focal && persons.length) {
    focal = [...persons].sort((a, b) => {
      const ay = Number(extractYear(a.birth_date)) || 0
      const by = Number(extractYear(b.birth_date)) || 0
      return by - ay
    })[0]
  }
  if (!focal) return null

  // Find the focal's PRIMARY partner — rule:
  //   1. Rels with no end_date AND no end_reason win (still-active).
  //   2. Among those, the most recent marriage_date.
  //   3. If none are active, fall back to the most recent marriage_date.
  //   4. Ties break on rel.id for determinism.
  // The multi-partner case (remarriages) therefore renders a single
  // clean line in the tree; all partners remain visible on the profile
  // page's RelationshipsPanel. Count of total partners is surfaced on
  // the focal card via the `partnerCount` return value.
  const allPartnerRels = relationships.filter(r =>
    r.type === 'partner' && (r.person_a_id === focal.id || r.person_b_id === focal.id))
  const partnerCount = allPartnerRels.length
  const primaryPartnerRel = pickPrimaryPartner(allPartnerRels)
  const partnerId = primaryPartnerRel
    ? (primaryPartnerRel.person_a_id === focal.id ? primaryPartnerRel.person_b_id : primaryPartnerRel.person_a_id)
    : null
  const partner = partnerId ? byId.get(partnerId) : null

  // Ancestor subtrees — real people only. Missing parents stop the branch.
  const leftTree = buildAncestorNode(focal, byId, relationships, 0, new Set())
  const rightTree = partner
    ? buildAncestorNode(partner, byId, relationships, 0, new Set())
    : null

  // Descendants tree — recurse through parent_child edges from focal downwards.
  // If focal has a partner, also include descendants reachable only via the
  // partner (rare but happens if children are linked to one parent only).
  const descendantsTree = buildDescendantsNode(focal, partner, byId, relationships, 0, new Set())

  // Focal's siblings — children of focal's parents, excluding focal+partner.
  // Rendered as standalone cards on the focal row so a leaf-of-the-line
  // person doesn't look stranded; clicking a sibling refocuses the tree.
  const siblings = findFocalSiblings(focal, partner, byId, relationships)

  return { focal, partner, leftTree, rightTree, descendantsTree, siblings, partnerCount }
}

function findFocalSiblings(focal, partner, byId, relationships) {
  const parentIds = new Set()
  for (const r of relationships) {
    if (r.type === 'parent_child' && r.person_b_id === focal.id) {
      parentIds.add(r.person_a_id)
    }
  }
  if (parentIds.size === 0) return []

  const sibIds = new Set()
  for (const r of relationships) {
    if (r.type !== 'parent_child') continue
    if (!parentIds.has(r.person_a_id)) continue
    const childId = r.person_b_id
    if (childId === focal.id) continue
    if (partner && childId === partner.id) continue
    sibIds.add(childId)
  }

  return [...sibIds]
    .map(id => byId.get(id))
    .filter(Boolean)
    .sort((a, b) => {
      const ay = Number(extractYear(a.birth_date)) || 9999
      const by = Number(extractYear(b.birth_date)) || 9999
      return ay - by
    })
}

// Primary-partner picker. Rules commented in the call site above.
function pickPrimaryPartner(rels) {
  if (!rels.length) return null
  const active = rels.filter(r => !r.end_date && !r.end_reason)
  const pool = active.length ? active : rels
  const sorted = [...pool].sort((a, b) => {
    const ay = Number(a.marriage_date?.match(/\d{4}/)?.[0] || 0)
    const by = Number(b.marriage_date?.match(/\d{4}/)?.[0] || 0)
    if (ay !== by) return by - ay // most recent wins
    return (a.id || '').localeCompare(b.id || '')
  })
  return sorted[0]
}

function buildAncestorNode(person, byId, relationships, depth, seen) {
  if (!person || seen.has(person.id)) return null
  const next = new Set(seen).add(person.id)

  // Collect parent candidates. Canonically a parent_child edge has
  // person_a = parent, person_b = child — so person_b_id === me finds parents.
  // But imports occasionally insert the edge reversed. To stay robust, also
  // accept a reversed edge (person_a_id === me) IF the other party is at
  // least 12 years older (plausible parent age gap). Ages <12 gap is almost
  // certainly a sibling/twin-mislabelled-as-parent situation and we leave
  // those alone to avoid corrupting the ancestry.
  const myYear = Number(extractYear(person.birth_date))
  const parents = []
  const seenParentIds = new Set()
  for (const r of relationships) {
    if (r.type !== 'parent_child') continue
    let otherId = null
    let canonical = false
    if (r.person_b_id === person.id) {
      otherId = r.person_a_id
      canonical = true
    } else if (r.person_a_id === person.id) {
      otherId = r.person_b_id
      canonical = false
    }
    if (!otherId || seenParentIds.has(otherId)) continue
    const other = byId.get(otherId)
    if (!other) continue
    if (canonical) {
      parents.push(other)
      seenParentIds.add(otherId)
      continue
    }
    // Reversed-edge heuristic: only accept if the other is clearly older.
    const otherYear = Number(extractYear(other.birth_date))
    if (!myYear || !otherYear) continue
    const gap = myYear - otherYear
    if (gap >= 12 && gap <= 80) {
      parents.push(other)
      seenParentIds.add(otherId)
    }
  }

  // Visual ordering: men on the left, women on the right. The d3.tree
  // layout positions parents in array order, so push the male first and
  // the female second. Same-sex / unknown-gender pairs fall back to
  // whatever order parents[] arrived in.
  const father = parents.find(x => x.gender === 'male')   || null
  const mother = parents.find(x => x.gender === 'female') || null
  const others = parents.filter(x => x !== father && x !== mother)
  const left  = father || others[0] || null
  const right = mother || others.find(x => x !== left) || null

  const node = { person, parents: [] }
  if (depth < MAX_ANC_DEPTH) {
    if (left) {
      const leftNode = buildAncestorNode(left, byId, relationships, depth + 1, next)
      if (leftNode) node.parents.push(leftNode)
    }
    if (right) {
      const rightNode = buildAncestorNode(right, byId, relationships, depth + 1, next)
      if (rightNode) node.parents.push(rightNode)
    }
  }
  return node
}

function buildDescendantsNode(person, partner, byId, relationships, depth, seen) {
  if (!person || seen.has(person.id)) return null
  const next = new Set(seen).add(person.id)
  if (partner && depth === 0) next.add(partner.id)

  // Children of this person (union with partner at depth 0 so the couple's
  // shared children aren't double-counted from the partner branch).
  const parentIds = new Set([person.id])
  if (partner && depth === 0) parentIds.add(partner.id)

  const childIdSet = new Set()
  for (const r of relationships) {
    if (r.type !== 'parent_child') continue
    if (parentIds.has(r.person_a_id)) childIdSet.add(r.person_b_id)
  }
  const childPersons = [...childIdSet]
    .map(id => byId.get(id))
    .filter(Boolean)
    .sort((a, b) => {
      const ay = Number(extractYear(a.birth_date)) || 9999
      const by = Number(extractYear(b.birth_date)) || 9999
      return ay - by
    })

  const node = { person, children: [] }
  if (depth < MAX_DESC_DEPTH) {
    for (const c of childPersons) {
      const childNode = buildDescendantsNode(c, null, byId, relationships, depth + 1, next)
      if (childNode) node.children.push(childNode)
    }
  }
  return node
}

function truncate(str, len) {
  return str.length > len ? str.slice(0, len - 1) + '…' : str
}

// Tag every node in an in-memory tree with its depth so the d3.hierarchy
// accessor can prune at a user-controlled cutoff without rebuilding the
// model on every slider tick.
function tagDepth(node, childrenOf, depth) {
  if (!node) return
  node.__depth = depth
  for (const c of (childrenOf(node) || [])) tagDepth(c, childrenOf, depth + 1)
}
